/**
 * FunnelPage — single shared template for the 4 placeholder funnels.
 *
 * Inputs:
 *   funnelKey: string                         — backend funnel_key value
 *   eyebrow, headline, body                   — hero copy
 *   comingNext                                — "coming soon" pill text
 *   noteLabel, notePlaceholder                — config for the optional textarea
 *
 * Behavior:
 *   - Public, no auth.
 *   - Reads ?source_lead_id from query string (set by the assessment outcome CTA)
 *     and includes it in the POST so we can attribute the inquiry.
 *   - POSTs to /api/funnels/{funnelKey}/inquiry; shows a confirmation card on success.
 *   - Email blocklist UX mirrors the backend.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../../assets/brand/kissed-logo.png";
import ConfirmCard from "../../components/funnel/ConfirmCard";
import { formatApiError } from "../../lib/formatError";
import {
  captureMarketingAttribution,
  readMarketingAttribution,
} from "../../lib/marketingAttribution";
import "./Funnel.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const BLOCKED_DOMAINS = new Set([
  "example.com", "test.com", "fake.com", "placeholder.com", "dummy.com",
  "mailinator.com", "guerrillamail.com", "tempmail.com", "throwaway.com",
]);

function emailShape(e) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim()); }
function emailBlocked(e) {
  if (!emailShape(e)) return false;
  const [local, domain] = e.trim().toLowerCase().split("@");
  if (BLOCKED_DOMAINS.has(domain)) return true;
  return local.includes("test_") || local.includes("test123");
}

export default function FunnelPage({
  funnelKey,
  eyebrow,
  headline,
  body,
  comingNext,
  noteLabel = "Anything we should know? (optional)",
  notePlaceholder = "Add any context that helps us reach out well…",
  testidPrefix,
}) {
  const location = useLocation();
  const sourceLeadId = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return q.get("source_lead_id") || q.get("lead") || "";
  }, [location.search]);

  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Sprint C — collapsed confirm flow when a known wellness lead is in the URL.
  const [lead, setLead] = useState(null);                // { first_name, email, pathway_assigned }
  const [leadCopy, setLeadCopy] = useState(null);        // { next_step_copy, booking_guidance, eyebrow, headline }
  const [leadLookupDone, setLeadLookupDone] = useState(false);
  const [legacyFormOverride, setLegacyFormOverride] = useState(false);

  useEffect(() => {
    captureMarketingAttribution();
    if (!sourceLeadId) { setLeadLookupDone(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const leadResp = await fetch(`${API_BASE}/wellness-assessment/lead/${sourceLeadId}`);
        if (!leadResp.ok) throw new Error(`lead lookup ${leadResp.status}`);
        const leadData = await leadResp.json();
        if (cancelled) return;

        // Pull the pathway-specific orchestration copy so the confirm card
        // surfaces the same "aligned next step" voice as the gateway outcome.
        let copy = null;
        try {
          const qResp = await fetch(`${API_BASE}/wellness-assessment/questions`);
          if (qResp.ok) {
            const qData = await qResp.json();
            const oc = (qData.outcomes || {})[leadData.pathway_assigned] || null;
            if (oc) {
              copy = {
                eyebrow: oc.eyebrow,
                headline: oc.headline,
                next_step_copy: oc.next_step_copy,
                booking_guidance: oc.booking_guidance,
                primary_cta_label: oc.primary_cta?.label || "Confirm & continue →",
              };
            }
          }
        } catch { /* tolerate copy lookup failures */ }
        if (!cancelled) {
          setLead(leadData);
          setLeadCopy(copy);
        }
      } catch {
        // Lead unknown or expired — fall back to legacy form (no PII exposure).
      } finally {
        if (!cancelled) setLeadLookupDone(true);
      }
    })();
    return () => { cancelled = true; };
  }, [sourceLeadId]);

  const shapeOk = emailShape(email);
  const blocked = shapeOk && emailBlocked(email);
  const emailValid = shapeOk && !blocked;
  const formValid = firstName.trim().length >= 1 && emailValid;

  async function submit(e) {
    e.preventDefault();
    if (!formValid) { setEmailTouched(true); return; }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = {
        first_name: firstName.trim(),
        email: email.trim(),
        note: note.trim(),
        source_lead_id: sourceLeadId || null,
      };
      const resp = await fetch(`${API_BASE}/funnels/${funnelKey}/inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(formatApiError(data, `HTTP ${resp.status}`));
      }
      setSubmitted(true);
    } catch (err) {
      setSubmitError(formatApiError(err, "Submission failed"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm({ note: confirmNote }) {
    const resp = await fetch(`${API_BASE}/funnels/${funnelKey}/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        source_lead_id: sourceLeadId,
        note: confirmNote || "",
        marketing_attribution: readMarketingAttribution() || null,
      }),
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(formatApiError(data, "We couldn't confirm just now. Please try again."));
    }
    return await resp.json();
  }

  function resetForAnother() {
    // Retained for compatibility — no longer surfaced in the new UX.
    setSubmitted(false);
    setFirstName("");
    setEmail("");
    setEmailTouched(false);
    setNote("");
    setSubmitError(null);
  }
  // eslint-disable-next-line no-unused-vars
  const _resetForAnother = resetForAnother;

  return (
    <div className="funnel-root" data-testid={`${testidPrefix}-page`}>
      <section className="funnel-hero">
        <div className="funnel-hero-inner">
          <div className="funnel-logo">
            <img src={logo} alt="KISSED by C.Von" />
          </div>
          <div className="funnel-eyebrow">{eyebrow}</div>
          <h1 className="funnel-display" data-testid={`${testidPrefix}-headline`}>{headline}</h1>
          <p>{body}</p>
          <div className="funnel-coming-pill" data-testid={`${testidPrefix}-coming-next`}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />
            {comingNext}
          </div>
        </div>
      </section>

      <section className="funnel-card-section">
        <div className="funnel-card" data-testid={`${testidPrefix}-card`}>
          {submitted ? (
            <div className="funnel-success" data-testid={`${testidPrefix}-success`}>
              <div className="funnel-checkmark">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 className="funnel-success-headline">You're in, {firstName || "friend"}.</h2>
              <p className="funnel-success-body">
                Here is your aligned next step — a member of the KISSED team will reach
                out within 1 business day to walk you through what comes next.
              </p>
              <div style={{ display: "flex", gap: 12, marginTop: 6 }}>
                <Link to="/" className="funnel-back-link">Back to KCIS</Link>
              </div>
            </div>
          ) : !leadLookupDone ? (
            <div className="funnel-confirm-loading" data-testid={`${testidPrefix}-loading`}>
              <div className="funnel-confirm-pulse" />
              <p>Loading your aligned next step…</p>
            </div>
          ) : lead && !legacyFormOverride ? (
            <ConfirmCard
              leadFirstName={lead.first_name}
              leadEmail={lead.email}
              funnelKey={funnelKey}
              funnelEyebrow={leadCopy?.eyebrow || eyebrow}
              funnelHeadline={leadCopy?.headline || headline}
              nextStepCopy={leadCopy?.next_step_copy}
              bookingGuidance={leadCopy?.booking_guidance}
              primaryCtaLabel={leadCopy?.primary_cta_label || "Confirm & continue →"}
              onConfirm={handleConfirm}
              onSwitchToLegacyForm={() => setLegacyFormOverride(true)}
              testidPrefix={`${testidPrefix}-confirm`}
            />
          ) : (
            <form onSubmit={submit}>
              <h2>Reserve your spot</h2>
              <p className="funnel-sub">
                Brief inquiry — under a minute. We'll follow up by email.
              </p>

              <div className="funnel-field">
                <label className="funnel-label" htmlFor={`${testidPrefix}-fn`}>First name</label>
                <input
                  id={`${testidPrefix}-fn`}
                  className="funnel-input"
                  data-testid={`${testidPrefix}-first-name`}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Your first name"
                  autoComplete="given-name"
                  required
                />
              </div>

              <div className="funnel-field">
                <label className="funnel-label" htmlFor={`${testidPrefix}-em`}>Email</label>
                <input
                  id={`${testidPrefix}-em`}
                  type="email"
                  className={`funnel-input ${emailTouched && !emailValid ? "funnel-input-error" : ""}`}
                  data-testid={`${testidPrefix}-email`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setEmailTouched(true)}
                  placeholder="you@yourdomain.com"
                  autoComplete="email"
                  required
                />
                {emailTouched && !emailValid && (
                  <p className="funnel-error-text" data-testid={`${testidPrefix}-email-error`}>
                    Please use a real email — placeholder / disposable addresses are not accepted.
                  </p>
                )}
              </div>

              <div className="funnel-field">
                <label className="funnel-label" htmlFor={`${testidPrefix}-note`}>{noteLabel}</label>
                <textarea
                  id={`${testidPrefix}-note`}
                  className="funnel-textarea"
                  data-testid={`${testidPrefix}-note`}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={notePlaceholder}
                  rows={4}
                />
              </div>

              {submitError && (
                <p className="funnel-error-text" data-testid={`${testidPrefix}-submit-error`}>{submitError}</p>
              )}

              <button
                type="submit"
                className="funnel-btn"
                data-testid={`${testidPrefix}-submit`}
                disabled={submitting || !formValid}
              >
                {submitting ? "Sending…" : "Submit Inquiry"}
              </button>

              {sourceLeadId && (
                <p style={{ marginTop: 12, fontSize: 11, color: "#7a7a7a" }}>
                  Source lead linked.
                </p>
              )}
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
